import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import backupNLPSetting from './backupNLPSetting';

/**
 * Helper function which gets NLPSetting
 */
const useNLPSetting = (applyBackupWhenNeeded = true) =>
  applyBackupWhenNeeded ? backupNLPSetting : null;

/**
 * Function returns an object that holds NLP constants or NULL if there is no data
 */
const useNLPConstants = (applyBackupWhenNeeded = true) => {
  const travelAgency = useSelector((store) => store.user.travelAgency);
  // Get NLP Constants
  const nlpConstants = useMemo(() => {
    if (travelAgency && travelAgency.nlpConstants) {
      return travelAgency.nlpConstants;
    }
    return applyBackupWhenNeeded ? backupNLPSetting.nlpConstants : null;
  }, [travelAgency, applyBackupWhenNeeded]);
  return nlpConstants;
};

/**
 * Function returns an object that holds NLP enums or NULL if there is no data
 */
const useNLPEnums = () => {
  // Get NLP Setting
  const nlpConstants = useNLPConstants();

  // Get NLP Enums
  const enums = useMemo(() => {
    const data = {};
    try {
      if (nlpConstants) {
        Object.entries(nlpConstants).forEach(([key, value]) => {
          if (value.type === 'Frontend Enum') {
            data[key] = JSON.parse(value.value);
          }
        });
      }
    } catch (ex) {
      console.log(ex);
      return null;
    }
    return Object.keys(data).length === 0 ? null : data;
  }, [nlpConstants]);
  return enums;
};

/**
 * Function returns an object that holds NLP relevant terms or NULL if there is no data
 */
const useNLPRelevantTerms = () => {
  // Get NLP Constants
  const nlpConstants = useNLPConstants();

  // Get NLP Relevant Terms
  const terms = useMemo(() => {
    const data = {};
    if (nlpConstants) {
      Object.entries(nlpConstants).forEach(([key, value]) => {
        if (value.type === 'Frontend Relevant Terms') {
          data[key] = value.value.split(/,\s*/);
        }
      });
    }
    return Object.keys(data).length === 0 ? null : data;
  }, [nlpConstants]);
  return terms;
};

/**
 * Function returns an object that holds NLP instructions or NULL if there is no data
 */
const useNLPInstructions = () => {
  // Get NLP Setting
  const nlpSetting = useNLPSetting();

  // Get NLP Instructions
  const nlpInstructions = useMemo(() => {
    if (nlpSetting && nlpSetting.nlpInstructions) {
      return nlpSetting.nlpInstructions;
    }
    return null;
  }, [nlpSetting]);
  return nlpInstructions;
};

/**
 * The placeholder value before nlpConstants is loaded
 */
const TEMP_NUMBER = 0;
const TEMP_STRING = '';
const TEMP_BOOLEAN = false;
const TEMP_ARRAY = [];

/**
 * Commonly shared column names for NLP instructions in BaseRow
 */
const RULE_COLUMN = 'nlpRules';
const MAP_COLUMN = 'mapForPlacingRules';

/**
 * A tool function for filtering and combining rules by type
 */
const combineRulesByType = (rules, type, prefix = '- ', joinBy = '\n') => {
  const combinedRules = rules
    .filter((rule) => rule.type === type)
    .map((rule) => `${prefix}${rule.value}`)
    .join(joinBy);
  return combinedRules;
};

/**
 * A tool function for getting map for placing rules for an instruction
 */
const getMapForPlacingRules = (nlpInstructions, instructionName) => {
  let mapForPlacingRules = TEMP_STRING;
  if (nlpInstructions && nlpInstructions[instructionName]) {
    mapForPlacingRules = nlpInstructions[instructionName][MAP_COLUMN];
  }
  return mapForPlacingRules;
};

/**
 * A tool function for filling all rules into the map.
 * However, after running the function, the map is likely partially filled only, because the map
 * sometimes contains placeholders for "constants", "data" or "reference to other instructions",
 * which needs to be replaced outside of this function.
 */
const fillRulesToMap = (nlpInstructions, instructionName, mapForPlacingRules) => {
  // Create a local copy of the map
  let map = mapForPlacingRules;

  // If nlpInstructions and rules are available, try to fill the map
  if (
    nlpInstructions &&
    nlpInstructions[instructionName] &&
    nlpInstructions[instructionName][RULE_COLUMN]
  ) {
    // Get all NLP rules of this instruction
    const nlpRules = nlpInstructions[instructionName][RULE_COLUMN];

    // The rule types that we have already visited
    const visitedTypes = [];

    // Loop over all the rules to combine unique types of rules and put into the map
    nlpRules.forEach((rule) => {
      const ruleType = rule.type;

      // Skip the visited rule types
      if (visitedTypes.includes(ruleType)) {
        return;
      }
      // Add the new rule type to the visited list
      visitedTypes.push(ruleType);

      // Combine rules of a type
      const combinedRules = combineRulesByType(nlpRules, ruleType);

      // Replace the placeholder in the map with the combined rules
      map = map.replace(new RegExp(`{{${ruleType}}}`, 'g'), combinedRules);
    });
  }
  // Return the partially filled map OR the original map if there is no data
  return map;
};

export {
  useNLPConstants,
  useNLPEnums,
  useNLPRelevantTerms,
  useNLPInstructions,
  TEMP_NUMBER,
  TEMP_STRING,
  TEMP_BOOLEAN,
  TEMP_ARRAY,
  RULE_COLUMN,
  MAP_COLUMN,
  combineRulesByType,
  getMapForPlacingRules,
  fillRulesToMap,
};
